import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";

import { CalculatorIcon } from "@heroicons/react/24/outline";

import QuoteCalculatorModal from "../../components/leads/QuoteCalculator/QuoteCalculatorModal";
import useCustomerDetails from "../../data/useCustomerDetails";
import { useQuoteCalculatorAccess } from "../../data/useQuoteCalculator";
import { useAuth } from "../../contexts/AuthContext";
import { wilmaSAVCustomerId } from "../../config";
import useQuotes from "../../data/useQuotes";
import { useCallBacks } from "../../data/useCallQueue";
import { usePageNameContext } from "../../contexts/PageTitleContext";
import { LeadStatus, QuoteTabType } from "../../models/Lead";
import QuotesTable from "../../components/leads/quotes/table/QuotesTable";
import QuoteStageFilter, { QuoteStage } from "../../components/leads/quotes/QuoteStageFilter";
import QuoteTypeTabs from "../../components/leads/quotes/QuoteTypeTabs";
import QuoteSearchFields from "../../components/leads/quotes/QuoteSearchFields";
import LiveQueueScreen from "../../components/leads/quotes/live/LiveQueueScreen";
import CallBacksList from "../../components/leads/quotes/live/callbacks/CallBacksList";
import ApprovalsBanner from "../../components/leads/quotes/live/approvals/ApprovalsBanner";
import PendingApprovalsPanel from "../../components/leads/quotes/live/approvals/PendingApprovalsPanel";
import classNames from "../../utils/classNames";

type QuotesView = "live" | "callbacks" | "all";

const VIEWS: { key: QuotesView; label: string }[] = [
  { key: "live", label: "Live queue" },
  { key: "callbacks", label: "Call backs" },
  { key: "all", label: "All quotes" },
];

export default function Quotes() {
  const { t } = useTranslation("leads");
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { setInfo } = usePageNameContext();
  const { customerId } = useAuth();
  const { features } = useCustomerDetails();
  // Only SAV can allocate ATF; other call centres are isolated within their own system.
  const isSavCustomer = !!customerId && customerId === wilmaSAVCustomerId;
  const { quoteStats, invalidateQuoteStats } = useQuotes("QuotesPage");
  const { callBacks } = useCallBacks(true, "QuotesPage");
  const view = (VIEWS.find((v) => v.key === searchParams.get("view"))?.key ?? "live") as QuotesView;
  const [activeQuoteTab, setActiveQuoteTab] = useState<QuoteTabType>(
    searchParams.get("tab") === "manager" ? QuoteTabType.ManagerAssistance : QuoteTabType.Quotes
  );
  const [activeQuoteStage, setActiveQuoteStage] = useState<QuoteStage | null>(null);

  const [sortAdditionalBody, setSortAdditionalBody] = useState<Record<string, any>>({});

  const [tableTotalRows, setTableTotalRows] = useState<number | null>(null);
  const [quoteCalculatorOpen, setQuoteCalculatorOpen] = useState(false);
  const { show: showQuoteCalculator } = useQuoteCalculatorAccess("Quotes");
  const isCallCentre = useMemo(
    () => !!features.data?.data?.find((f) => f.name === "Call Centre"),
    [features.data]
  );

  useEffect(() => {
    if (!features.isLoading && !isCallCentre) {
      navigate("/", { replace: true });
      return;
    }

    setInfo({
      name: t("Quotes"),
      desc: t("Call web quotes one at a time and book them"),
    });
  }, [features.isLoading, isCallCentre, navigate, t]);

  const setView = (next: QuotesView) => {
    const params = new URLSearchParams(searchParams);
    params.set("view", next);
    params.delete("tab");
    setSearchParams(params, { replace: true });
  };

  const additionalBody = useMemo(() => ({
    Status: LeadStatus["Call List"],
    QuoteStep: activeQuoteStage,
    QuoteTabType: activeQuoteTab,
    AwaitingCod: true,
    IncludeCompleted: false,
    DocumentFilter: 6, // All
    // The old call-list stage is no longer used, but the API's quote filter still defaults to
    // "CallListStatus is null"; sending nulls switches it off so every quote is listed.
    CallListStatus: null,
    CallListStatusIsNull: null,
    ...sortAdditionalBody,
  }), [activeQuoteStage, activeQuoteTab, sortAdditionalBody]);

  const quoteStageCounts = useMemo(() => {
    const stats = quoteStats.data?.data;
    if (!stats) return {};

    return {
      1: stats.quoteStageOne,
      2: stats.quoteStageTwo,
      3: stats.quoteStageThree,
      4: stats.quoteStageFour,
      5: stats.quoteStageFive,
      6: stats.quoteStageSix,
      7: stats.quoteStageSeven,
    } as Partial<Record<QuoteStage, number>>;
  }, [quoteStats.data?.data]);

  const quoteTabCounts = useMemo(() => {
    const stats = quoteStats.data?.data;
    if (!stats) return {};

    return {
      [QuoteTabType.Quotes]: stats.quotesTab,
      [QuoteTabType.Atf]: stats.atfTab,
      [QuoteTabType.HighValue]: stats.highValueTab,
      [QuoteTabType.HighPriority]: stats.highPriorityTab,
      [QuoteTabType.ManagerAssistance]: stats.managerAssistanceTab,
      [QuoteTabType.CustomerCallingBack]: stats.customerCallingBackTab,
    } as Partial<Record<QuoteTabType, number>>;
  }, [quoteStats.data?.data]);

  const selectedQuoteStageStatCount = useMemo(() => {
    if (!activeQuoteStage) return null;
    const value = quoteStageCounts[activeQuoteStage];
    return typeof value === "number" ? value : null;
  }, [activeQuoteStage, quoteStageCounts]);

  const selectedQuoteTabStatCount = useMemo(() => {
    const value = quoteTabCounts[activeQuoteTab];
    return typeof value === "number" ? value : null;
  }, [activeQuoteTab, quoteTabCounts]);

  useEffect(() => {
    if (tableTotalRows === null) return;

    if (activeQuoteStage && selectedQuoteStageStatCount !== null && tableTotalRows !== selectedQuoteStageStatCount) {
      invalidateQuoteStats();
      return;
    }

    if (!activeQuoteStage && selectedQuoteTabStatCount !== null && tableTotalRows !== selectedQuoteTabStatCount) {
      invalidateQuoteStats();
    }
  }, [
    activeQuoteStage,
    activeQuoteTab,
    tableTotalRows,
    selectedQuoteStageStatCount,
    selectedQuoteTabStatCount,
    invalidateQuoteStats,
  ]);

  const callBacksDue = (callBacks.data?.data ?? []).filter((x) => x.isDue).length;

  if (!isCallCentre) return null;

  return (
    <div className="space-y-4">
      <ApprovalsBanner />

      <div className="flex flex-wrap items-center justify-between gap-2">
      <nav aria-label={t("Quote views")} className="inline-flex flex-wrap gap-1 rounded-lg bg-gray-100 p-1">
        {VIEWS.map((v) => (
          <button
            key={v.key}
            type="button"
            onClick={() => setView(v.key)}
            aria-current={view === v.key ? "page" : undefined}
            className={classNames(
              "flex min-h-[36px] items-center gap-1.5 rounded-md px-3 py-1.5 text-[13px] font-medium",
              view === v.key ? "bg-primary-600 text-white" : "text-gray-700 hover:bg-white"
            )}
          >
            {t(v.label)}
            {v.key === "callbacks" && callBacksDue > 0 && (
              <span className="rounded-full border border-amber-200 bg-amber-50 px-1.5 text-[11px] font-semibold text-amber-800">
                {t("{{count}} due", { count: callBacksDue })}
              </span>
            )}
          </button>
        ))}
      </nav>

        {showQuoteCalculator && (
          <button
            type="button"
            onClick={() => setQuoteCalculatorOpen(true)}
            className="inline-flex items-center rounded-md border border-transparent bg-primary-600 px-3 py-2 text-sm font-medium leading-4 text-white shadow-sm hover:bg-primary-700"
          >
            <CalculatorIcon className="-ml-0.5 mr-2 h-4 w-4" aria-hidden="true" />
            {t("Get Quote")}
          </button>
        )}
      </div>

      {quoteCalculatorOpen && <QuoteCalculatorModal open={quoteCalculatorOpen} setOpen={setQuoteCalculatorOpen} />}

      {view === "live" && <LiveQueueScreen />}

      {view === "callbacks" && <CallBacksList />}

      {view === "all" && (
        <>
          <QuoteStageFilter
            setActiveQuoteStage={setActiveQuoteStage}
            activeQuoteStage={activeQuoteStage}
            totalQuoteStages={quoteStats.data?.data?.totalQuoteStages}
            stageCounts={quoteStageCounts}
          />
          <QuoteTypeTabs
            activeTab={activeQuoteTab}
            setActiveTab={setActiveQuoteTab}
            tabCounts={quoteTabCounts}
            showAtfTab={isSavCustomer}
          />
          {activeQuoteTab === QuoteTabType.ManagerAssistance && <PendingApprovalsPanel />}
          <QuoteSearchFields sortAdditionalBody={sortAdditionalBody} setSortAdditionalBody={setSortAdditionalBody} />
          <QuotesTable additionalBody={additionalBody} onTotalRowsChange={setTableTotalRows} />
        </>
      )}
    </div>
  );
}
